import type { CaptureResult, Properties } from 'posthog-js';

/**
 * Session replay masks all text by default (fail-closed). Add `ph-unmask` to a container to
 * reveal its text, and `ph-mask` inside it to hide a PII region again; the nearest marker wins.
 */
export const REPLAY_UNMASK_CLASS = 'ph-unmask';
export const REPLAY_MASK_CLASS = 'ph-mask';
/**
 * Input values stay masked even inside `ph-unmask`. Add this to an input, or to a component that
 * wraps one, to record its value; `ph-mask` hides it again and the nearest marker wins. Use it only
 * for read-only product data, never for anything a user types about an investor.
 */
export const REPLAY_UNMASK_INPUT_CLASS = 'ph-unmask-input';

/**
 * Elements replay replaces with an empty placeholder, on top of anything marked `ph-no-capture`.
 * Document viewers render whole investor documents, and thumbnails are images of them: replay
 * records an image's `src`, which text masking cannot hide. Hidden inputs are the one input type
 * `maskAllInputs` does not mask.
 */
export const REPLAY_BLOCK_SELECTOR = [
    'aix-pdf',
    'aix-pdf-viewer',
    'aix-image-viewer',
    'aix-signature-overlay',
    'aix-ui-document-viewer',
    'aix-document-viewer',
    'aix-order-form-preview',
    'aix-thumbnail',
    'input[type="hidden"]'
].join(', ');

// rrweb event types (https://github.com/rrweb-io/rrweb/blob/master/packages/types/src/index.ts)
const RRWEB_META_EVENT = 4;
const RRWEB_CUSTOM_EVENT = 5;

export function maskReplayText(text: string, element?: Element): string {
    const nearestMarker = element?.closest(`.${REPLAY_MASK_CLASS}, .${REPLAY_UNMASK_CLASS}`);
    if (nearestMarker?.classList.contains(REPLAY_UNMASK_CLASS)) {
        return text;
    }

    return text.replace(/\S/g, '*');
}

export function maskReplayInput(value: string, element?: HTMLElement): string {
    const nearestMarker = element?.closest(`.${REPLAY_MASK_CLASS}, .${REPLAY_UNMASK_INPUT_CLASS}`);
    const isPassword = element instanceof HTMLInputElement && element.type === 'password';
    if (!isPassword && nearestMarker?.classList.contains(REPLAY_UNMASK_INPUT_CLASS)) {
        return value;
    }

    return '*'.repeat(value.length);
}

/** Attributes that hold readable text, so replay records them only where it would record text. */
const TEXT_ATTRIBUTES = new Set([
    'title',
    'alt',
    'placeholder',
    'label',
    'data-title',
    'data-tooltip',
    'data-original-title'
]);
/**
 * ARIA attributes whose values are only tokens, numbers or element ids, so replay records them.
 * Every other `aria-*` attribute is treated as text (fail-closed), including ones added to ARIA
 * later, such as `aria-braillelabel`.
 */
const ARIA_TOKEN_ATTRIBUTES = new Set([
    'aria-activedescendant',
    'aria-atomic',
    'aria-autocomplete',
    'aria-busy',
    'aria-checked',
    'aria-colcount',
    'aria-colindex',
    'aria-colspan',
    'aria-controls',
    'aria-current',
    'aria-describedby',
    'aria-details',
    'aria-disabled',
    'aria-dropeffect',
    'aria-errormessage',
    'aria-expanded',
    'aria-flowto',
    'aria-grabbed',
    'aria-haspopup',
    'aria-hidden',
    'aria-invalid',
    'aria-labelledby',
    'aria-level',
    'aria-live',
    'aria-modal',
    'aria-multiline',
    'aria-multiselectable',
    'aria-orientation',
    'aria-owns',
    'aria-posinset',
    'aria-pressed',
    'aria-readonly',
    'aria-relevant',
    'aria-required',
    'aria-rowcount',
    'aria-rowindex',
    'aria-rowspan',
    'aria-selected',
    'aria-setsize',
    'aria-sort',
    'aria-valuemax',
    'aria-valuemin',
    'aria-valuenow'
]);

function isTextAttribute(key: string): boolean {
    return TEXT_ATTRIBUTES.has(key) || (key.startsWith('aria-') && !ARIA_TOKEN_ATTRIBUTES.has(key));
}
const CONTACT_LINK = /^(mailto|tel|sms):/i;
/** Route parameters (`;search=...`) and query strings, which can carry what a user typed. */
const LINK_PARAMETERS = /[;?].*$/;

/**
 * Replay records element attributes as written, so a tooltip or a link could carry what text
 * masking hides. Readable attributes follow the text rule, contact links too, and other links keep
 * their path without parameters. Input values are left to `maskReplayInput`.
 */
export function maskReplayAttribute(name: string, value: string, element?: Element): string {
    const key = name.toLowerCase();
    if (key === 'href') {
        return CONTACT_LINK.test(value)
            ? maskReplayText(value, element)
            : value.replace(LINK_PARAMETERS, '');
    }
    const isFormValue =
        element instanceof HTMLInputElement ||
        element instanceof HTMLTextAreaElement ||
        element instanceof HTMLSelectElement;
    if (isTextAttribute(key) || (key === 'value' && !isFormValue)) {
        return maskReplayText(value, element);
    }

    return value;
}

/** Auth0 implicit-flow callback parameters, in any route a token could hide in. */
const AUTH_HASH_PARAMS = /access_token|id_token|token_type/i;
/** More decoding rounds than any real route needs; a hash still changing after them is dropped. */
const MAX_HASH_DECODES = 5;

/**
 * Decodes a hash until decoding no longer changes it, so encoding it more than once cannot hide a
 * parameter name or `=`. Returns undefined when the hash cannot be decoded or never settles.
 */
function decodeHash(hash: string): string | undefined {
    let decoded = hash;
    for (let round = 0; round < MAX_HASH_DECODES; round++) {
        let next: string;
        try {
            next = decodeURIComponent(decoded);
        } catch {
            return undefined;
        }
        if (next === decoded) {
            return decoded;
        }
        decoded = next;
    }

    return undefined;
}

/**
 * Whether a hash is an app route rather than parameters. The Auth0 callback lands on
 * `#access_token=...`, and the hash router then rewrites it to `#/access_token%3D...`, which looks
 * like a route until it is decoded, so the check runs on the fully decoded hash.
 */
function isRouteHash(hash: string): boolean {
    const decoded = decodeHash(hash);
    return decoded !== undefined && /^#\/[^=]*$/.test(decoded) && !AUTH_HASH_PARAMS.test(decoded);
}

/**
 * Drops every query string and any non-route hash from a URL. The app uses hash routing, so a
 * route like `#/orders/123` is kept, but Auth0's implicit-flow callback lands on
 * `#access_token=...&id_token=...`, which must never leave the browser.
 */
export function scrubUrl(value: string): string {
    let url: URL;
    try {
        url = new URL(value);
    } catch {
        return value;
    }

    const route = url.hash.split('?')[0];
    url.search = '';
    url.hash = isRouteHash(route) ? route : '';
    url.username = '';
    url.password = '';
    return url.toString();
}

function scrubUrlProperties(properties: Properties | undefined): void {
    if (!properties) {
        return;
    }

    for (const [key, value] of Object.entries(properties)) {
        if (typeof value === 'string' && /^https?:\/\//i.test(value)) {
            properties[key] = scrubUrl(value);
        }
    }
}

function scrubSnapshotUrls(snapshots: unknown): void {
    if (!Array.isArray(snapshots)) {
        return;
    }

    for (const snapshot of snapshots) {
        if (snapshot?.type === RRWEB_META_EVENT && typeof snapshot.data?.href === 'string') {
            snapshot.data.href = scrubUrl(snapshot.data.href);
        }
        if (snapshot?.type === RRWEB_CUSTOM_EVENT) {
            scrubUrlProperties(snapshot.data?.payload);
        }
    }
}

const URL_IN_TEXT = /https?:\/\/[^\s'"<>]+/gi;
// Punctuation that ends a sentence rather than a URL, as in `for https://host/api?q=x: 500`.
const URL_TRAILING_PUNCTUATION = /[.,;:!?)\]]+$/;
const EMAIL_IN_TEXT = /[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}/gi;

/**
 * Scrubs every URL inside free text and replaces email addresses. Error messages quote request
 * URLs, such as Angular's `Http failure response for <url>: 500`, whose query strings can carry
 * search terms, and backend errors can quote the email they were about.
 */
export function scrubText(text: string): string {
    return text
        .replace(URL_IN_TEXT, match => {
            const trailing = URL_TRAILING_PUNCTUATION.exec(match)?.[0] ?? '';
            return scrubUrl(match.slice(0, match.length - trailing.length)) + trailing;
        })
        .replace(EMAIL_IN_TEXT, '[email]');
}

interface CapturedException {
    value?: unknown;
    stacktrace?: { frames?: { filename?: unknown; abs_path?: unknown }[] };
}

/** `$exception_list` holds each error in the cause chain, with its message and stack frames. */
function scrubExceptions(properties: Properties | undefined): void {
    if (!properties) {
        return;
    }

    if (typeof properties['$exception_message'] === 'string') {
        properties['$exception_message'] = scrubText(properties['$exception_message']);
    }

    const exceptions: unknown = properties['$exception_list'];
    if (!Array.isArray(exceptions)) {
        return;
    }

    for (const exception of exceptions as CapturedException[]) {
        if (typeof exception?.value === 'string') {
            exception.value = scrubText(exception.value);
        }
        for (const frame of exception?.stacktrace?.frames ?? []) {
            if (typeof frame.filename === 'string') {
                frame.filename = scrubUrl(frame.filename);
            }
            if (typeof frame.abs_path === 'string') {
                frame.abs_path = scrubUrl(frame.abs_path);
            }
        }
    }
}

/**
 * `before_send` hook: scrubs URLs on analytics events and on session replay snapshots, and URLs
 * and email addresses inside exception messages.
 */
export function scrubCapturedUrls(event: CaptureResult | null): CaptureResult | null {
    if (!event) {
        return event;
    }

    scrubUrlProperties(event.properties);
    scrubUrlProperties(event.properties?.['$set']);
    scrubUrlProperties(event.properties?.['$set_once']);
    scrubUrlProperties(event.$set);
    scrubUrlProperties(event.$set_once);
    scrubSnapshotUrls(event.properties?.['$snapshot_data']);
    if (event.event === '$exception') {
        scrubExceptions(event.properties);
    }
    return event;
}
